export type PaymentSuccessMethod = 'online' | 'wallet';

export interface PaymentSuccessDetails {
  courseId: string;
  courseName?: string;
  amount: number;
  currency?: string;
  paymentMethod: PaymentSuccessMethod;
  orderId?: string;
  transactionId?: string;
  promoCode?: string;
  discountAmount?: number;
  originalPrice?: number;
  coinsSpent?: number;
  balanceAfter?: number;
  paidAt?: string;
}

export const PENDING_COURSE_PAYMENT_KEY = 'pendingCoursePayment';

export function formatEgyptianPaymentDate(date = new Date()): string {
  return date.toLocaleString('ar-EG', {
    timeZone: 'Africa/Cairo',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function getPaymentMethodLabel(method: PaymentSuccessMethod): string {
  return method === 'wallet' ? 'الدفع من المحفظة' : 'بوابة الدفع الإلكترونية';
}

export function savePendingCoursePayment(details: Omit<PaymentSuccessDetails, 'paidAt'>): void {
  sessionStorage.setItem(
    PENDING_COURSE_PAYMENT_KEY,
    JSON.stringify({ ...details, paidAt: new Date().toISOString() })
  );
}

export function loadPendingCoursePayment(): PaymentSuccessDetails | null {
  try {
    const raw = sessionStorage.getItem(PENDING_COURSE_PAYMENT_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as PaymentSuccessDetails;
  } catch {
    return null;
  }
}

export function clearPendingCoursePayment(): void {
  sessionStorage.removeItem(PENDING_COURSE_PAYMENT_KEY);
}

export function parsePaymentSuccessFromSearchParams(
  params: URLSearchParams
): PaymentSuccessDetails | null {
  const courseId = params.get('courseId') ?? params.get('CourseId');
  if (!courseId) return null;

  const success =
    params.get('success') === 'true' ||
    params.get('payment') === 'success' ||
    params.get('status') === 'success' ||
    params.get('status') === 'paid';

  if (!success && !params.get('orderId') && !params.get('transactionId')) {
    return null;
  }

  const methodParam = (params.get('method') ?? params.get('paymentMethod') ?? 'online').toLowerCase();

  return {
    courseId,
    courseName: params.get('courseName') ?? params.get('CourseName') ?? undefined,
    amount: Number(params.get('amount') ?? params.get('netPrice') ?? 0),
    currency: params.get('currency') ?? 'EGP',
    paymentMethod: methodParam === 'wallet' ? 'wallet' : 'online',
    orderId: params.get('orderId') ?? params.get('OrderId') ?? undefined,
    transactionId: params.get('transactionId') ?? params.get('TransactionId') ?? undefined,
    promoCode: params.get('promoCode') ?? undefined,
    discountAmount: params.get('discountAmount')
      ? Number(params.get('discountAmount'))
      : undefined,
    originalPrice: params.get('originalPrice') ? Number(params.get('originalPrice')) : undefined,
    coinsSpent: params.get('coinsSpent') ? Number(params.get('coinsSpent')) : undefined,
    balanceAfter: params.get('balanceAfter') ? Number(params.get('balanceAfter')) : undefined,
    paidAt: params.get('paidAt') ?? new Date().toISOString(),
  };
}
